// CATNET1: shared base facts only. Local operating values never enter these DTOs.
export interface CatalogKind {
  id: string;
  key: string;
  label: string;
  mechanism: "per_ounce" | "whole_unit" | "both";
  active: boolean;
}
export interface CatalogCategory {
  id: string;
  kind_id: string;
  key: string;
  label: string;
  active: boolean;
}
export interface CatalogProduct {
  product_id: string;
  revision: number;
  name: string;
  brand: string | null;
  kind_id: string;
  category_id: string;
  package_class: string;
  size_value: string;
  size_unit: string;
  units_per_package: number;
  upc: string | null;
  active: boolean;
}
export interface CatalogView {
  ownerKind: "network" | "independent";
  catalog: {
    id: string;
    state: string;
    version: number;
    ownerKind: "network" | "independent";
  } | null;
  kinds: CatalogKind[];
  categories: CatalogCategory[];
  products: CatalogProduct[];
}
export interface CatalogLocalItem {
  localProductId: string;
  name: string;
  kindKey: string;
  categoryKey: string;
  sizeValue: string | null;
  sizeUnit: string | null;
  packageClass: string | null;
  upc: string | null;
  active: boolean;
  link: null | {
    productId: string;
    confirmedRevision: number;
    currentRevision: number;
    version: number;
  };
}
export interface CatalogProposal {
  name: string;
  brand: string | null;
  kindId: string;
  categoryId: string;
  packageClass: string;
  sizeValue: string;
  sizeUnit: string;
  unitsPerPackage: number;
  upc: string | null;
}
export interface CatalogRequest {
  id: string;
  locationId: string;
  locationName: string;
  proposal: CatalogProposal;
  state: string;
  version: number;
  resolvedProductId: string | null;
  reason: string | null;
  createdAt: string;
}
export interface CatalogReply {
  status: number;
  body: unknown;
}
export function catalogPackage(p: CatalogProduct): string {
  return `${p.units_per_package > 1 ? `${p.units_per_package} × ` : ""}${p.size_value} ${p.size_unit} · ${p.package_class}`;
}
export function catalogProposal(p: CatalogProduct): CatalogProposal {
  return {
    name: p.name,
    brand: p.brand,
    kindId: p.kind_id,
    categoryId: p.category_id,
    packageClass: p.package_class,
    sizeValue: p.size_value,
    sizeUnit: p.size_unit,
    unitsPerPackage: p.units_per_package,
    upc: p.upc,
  };
}
export function catalogCompatible(
  item: CatalogLocalItem,
  p: CatalogProduct,
  view: CatalogView,
): boolean {
  const kind = view.kinds.find((k) => k.id === p.kind_id && k.active);
  const category = view.categories.find(
    (c) => c.id === p.category_id && c.kind_id === p.kind_id && c.active,
  );
  return (
    p.active &&
    p.units_per_package === 1 &&
    kind?.key === item.kindKey &&
    category?.key === item.categoryKey &&
    item.sizeValue !== null &&
    item.sizeUnit !== null &&
    item.packageClass !== null &&
    Number(item.sizeValue) === Number(p.size_value) &&
    item.sizeUnit === p.size_unit &&
    item.packageClass === p.package_class
  );
}
export function isCatalogView(value: unknown): value is CatalogView {
  if (value === null || typeof value !== "object") return false;
  const v = value as Partial<CatalogView>;
  return (
    ["network", "independent"].includes(v.ownerKind ?? "") &&
    Array.isArray(v.products) &&
    Array.isArray(v.kinds) &&
    Array.isArray(v.categories) &&
    (v.catalog === null ||
      (typeof v.catalog?.id === "string" &&
        ["network", "independent"].includes(v.catalog.ownerKind)))
  );
}

export interface CatalogLocalCreateSelection {
  product: CatalogProduct;
  kindKey: string;
  categoryKey: string;
  localSaleMechanism: "per_ounce" | "whole_unit";
  requestId: string;
}

export interface CatalogActivationIdentity {
  networkId: string;
  accountId: string;
}
export interface CatalogActivationIntent {
  catalogId: string;
  requestId: string;
  expectedVersion: number;
}
export interface CatalogActivationStatus {
  catalog: { id: string; state: "preparation" | "enforced"; version: number } | null;
  ready: boolean;
  reason:
    | "catalog_unavailable"
    | "already_active"
    | "deployment_unavailable"
    | "ownership_review_required"
    | null;
  outcome?: "applied" | "retry_same_request" | "not_applied" | "unavailable";
  resultingVersion?: number;
}
const activationUuid = (value: unknown): value is string =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
const activationVersion = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 1;
export function isCatalogActivationStatus(value: unknown): value is CatalogActivationStatus {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  if (
    Object.keys(v).some(
      (key) => !["catalog", "ready", "reason", "outcome", "resultingVersion"].includes(key),
    ) ||
    typeof v.ready !== "boolean" ||
    ![
      null,
      "catalog_unavailable",
      "already_active",
      "deployment_unavailable",
      "ownership_review_required",
    ].includes(v.reason as string | null)
  )
    return false;
  if (v.catalog !== null) {
    if (typeof v.catalog !== "object" || Array.isArray(v.catalog) || v.catalog === undefined)
      return false;
    const c = v.catalog as Record<string, unknown>;
    if (
      Object.keys(c).some((key) => !["id", "state", "version"].includes(key)) ||
      !activationUuid(c.id) ||
      !activationVersion(c.version) ||
      !["preparation", "enforced"].includes(String(c.state))
    )
      return false;
  }
  const catalog = v.catalog as CatalogActivationStatus["catalog"];
  if (v.ready && (catalog?.state !== "preparation" || v.reason !== null)) return false;
  if (
    v.outcome !== undefined &&
    !["applied", "retry_same_request", "not_applied", "unavailable"].includes(String(v.outcome))
  )
    return false;
  if (v.outcome === "applied")
    return (
      !v.ready &&
      v.reason === null &&
      catalog?.state === "enforced" &&
      activationVersion(v.resultingVersion) &&
      catalog.version >= v.resultingVersion
    );
  if (v.resultingVersion !== undefined) return false;
  if (v.outcome === "retry_same_request")
    return v.ready === true && catalog?.state === "preparation";
  if (v.outcome === "not_applied")
    return v.ready === false && v.reason === null && catalog !== null;
  if (v.outcome === "unavailable") return v.ready === false && typeof v.reason === "string";
  return catalog === null
    ? v.ready === false && v.reason === "catalog_unavailable"
    : catalog.state === "enforced"
      ? v.ready === false && v.reason === "already_active"
      : v.ready
        ? v.reason === null
        : ["deployment_unavailable", "ownership_review_required"].includes(String(v.reason));
}
export function isCatalogActivationResult(
  value: unknown,
): value is { id: string; state: "enforced"; version: number; replayed: boolean } {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  return (
    Object.keys(v).every((key) => ["id", "state", "version", "replayed"].includes(key)) &&
    activationUuid(v.id) &&
    v.state === "enforced" &&
    activationVersion(v.version) &&
    typeof v.replayed === "boolean"
  );
}

/** HQFRAN1 (B3): the key a kind or category gets from its name, so a person never types one. "Craft Beer" gives "craft_beer".
 * It matches the server's [a-z][a-z0-9_]* rule; a name with no letters or digits gives "". */
export function keyFromName(name: string): string {
  const base = name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (base === "") return "";
  return /^[a-z]/.test(base) ? base : `k_${base}`;
}
